import { useEffect, useRef, useState } from 'react';
import {
  Modal,
  StatusBar,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import { COLORS, IS_IOS } from '../../constants';
import {
  selectDivRadius,
  selectFontFamily,
  selectMainColor,
} from '../../features/appconfig/store/selectors';
import { getFontFamily } from '../../helpers/fontMapper';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import i18n from '../../localization/i18n';
import { CustomIcon } from '../CustomIcon';
import CustomText from '../CustomText';
import { ScreenLayout } from '../ScreenLayout';

interface Props {
  visible: boolean;
  initialValue?: string;
  onCancel: () => void;
  onSave: (term: string) => void;
}

export function SearchModal({ visible, initialValue = '', onCancel, onSave }: Props) {
  const [term, setTerm] = useState<string>(initialValue);
  const inputRef = useRef<TextInput>(null);
  const mainColor = useAppSelector(selectMainColor);
  const fontFamily = useAppSelector(selectFontFamily);
  const divRadius = useAppSelector(selectDivRadius);
  const { bodyHorizontalPadding } = useResponsiveLayout();
  const insets = useSafeAreaInsets();

  useEffect(() => {
    setTerm(initialValue);
  }, [initialValue, visible]);

  useEffect(() => {
    if (visible) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [visible]);

  return (
    <Modal
      animationType="slide"
      visible={visible}
      presentationStyle="fullScreen"
      statusBarTranslucent={true}
      onRequestClose={onCancel}
    >
      <StatusBar
        backgroundColor={COLORS.TRANSPARENT}
        barStyle="light-content"
        translucent
      />
      <ScreenLayout
        headerStyle={IS_IOS && { marginTop: insets.top }}
        headerContents={
          <View style={styles.toolbar}>
            <TouchableOpacity
              onPress={onCancel}
              style={styles.backBtn}
              accessibilityLabel={i18n.t('nav.back')}
              activeOpacity={0.7}
            >
              <CustomIcon name="back" size={24} color={COLORS.WHITE} />
            </TouchableOpacity>
            <View style={[styles.toolbarSearch, { borderRadius: divRadius }]}>
              <CustomIcon name="search" color="#C5C5C7" size={mvs(13)} />
              <TextInput
                ref={inputRef}
                style={[
                  styles.toolbarTextInput,
                  { fontFamily: getFontFamily(fontFamily, 'Regular') },
                ]}
                value={term}
                onChangeText={setTerm}
                placeholder={i18n.t('songHistory.searchPlaceholder')}
                placeholderTextColor={'#C5C5C7'}
                returnKeyType="search"
                onSubmitEditing={() => onSave(term)}
                selectionColor={mainColor}
              />
            </View>
          </View>
        }
      >
        <View style={[styles.content, { paddingHorizontal: bodyHorizontalPadding }]}>
          <CustomText style={styles.emptyText} weight="Bold">
            {i18n.t('songHistory.searchNoResults')}
          </CustomText>
        </View>
      </ScreenLayout>
    </Modal>
  );
}

const styles = StyleSheet.create({
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backBtn: {
    marginRight: mvs(11),
  },
  toolbarSearch: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.WHITE,
    paddingHorizontal: mvs(16),
    paddingVertical: mvs(12),
  },
  toolbarTextInput: {
    flex: 1,
    color: '#474747',
    fontSize: mvs(16),
    paddingLeft: mvs(8),
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    textAlign: 'center',
    color: COLORS.WHITE,
    fontSize: mvs(24),
  },
});
